/**
 * /change-password — password change.
 *
 * Two scenarios use this page:
 *   1. Forced (must_change_password=true): user just signed in with
 *      a temp password set by an admin, or just consumed a reset link.
 *      → Auth-style full-screen experience (AuthLayout) — signals
 *      "this is a required setup step before you can use the app".
 *
 *   2. Voluntary: user chose "Update password" from their Profile.
 *      → Normal app chrome (SiteLayout) with a settings-style card.
 *      Industry standard — same pattern as GitHub, Linear, Stripe etc.
 *
 * The form is shared between the two scenarios; only the surrounding
 * shell changes.
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyIcon from '@mui/icons-material/Key';
import { Alert, Box, Button, Container, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth';
import { SiteLayout } from '../../components/SiteLayout';
import { AuthLayout } from './AuthLayout';
import { AuthPrimaryButton } from './AuthPrimaryButton';
import { AuthTextField } from './AuthTextField';
import { describeError } from './errorMessages';

const INK = '#0f0a07';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function ChangePasswordPage() {
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && next !== confirm;
  const forced = user?.must_change_password ?? false;
  const valid = current.length > 0 && next.length >= 8 && next === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    setSubmitting(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      navigate(forced ? '/' : '/profile', { replace: true });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Forced scenario: full-screen auth-style layout ──────────
  if (forced) {
    return (
      <AuthLayout
        title="Set your password"
        subtitle="Choose a password before continuing to your dashboard."
      >
        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <AuthTextField
              label="Current password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              autoFocus
            />

            <AuthTextField
              label="New password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              autoComplete="new-password"
            />

            <AuthTextField
              label="Confirm new password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              error={mismatch}
              helperText={mismatch ? 'Passwords do not match.' : ' '}
            />

            <AuthPrimaryButton type="submit" disabled={!valid} loading={submitting}>
              Update password
            </AuthPrimaryButton>
          </Stack>
        </form>
      </AuthLayout>
    );
  }

  // ─── Voluntary scenario: settings-style card under SiteLayout ─
  return (
    <SiteLayout subtitle="Account">
      <Container maxWidth="sm" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 5, md: 8 } }}>
        {/* Back link */}
        <RouterLink
          to="/profile"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            textDecoration: 'none',
            color: 'rgba(15, 10, 7, 0.55)',
            fontFamily: FONT_INTER,
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 16,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back to profile
        </RouterLink>

        {/* Page heading */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontWeight: 700,
              fontSize: { xs: 24, md: 28 },
              letterSpacing: '-0.015em',
              color: INK,
            }}
          >
            Update password
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontFamily: FONT_INTER,
              fontSize: 14,
              color: 'rgba(15, 10, 7, 0.6)',
            }}
          >
            Pick a new password — at least 8 characters.
          </Typography>
        </Box>

        {/* Form card */}
        <Box
          sx={{
            position: 'relative',
            bgcolor: IVORY_LIGHT,
            border: '1px solid rgba(187, 140, 63, 0.18)',
            borderRadius: 2,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
            },
          }}
        >
          {/* Header strip */}
          <Box
            sx={{
              px: { xs: 2.5, md: 3 },
              pt: { xs: 2.25, md: 2.5 },
              pb: { xs: 1.75, md: 2 },
              borderBottom: '1px solid rgba(15, 10, 7, 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'rgba(187, 140, 63, 0.12)',
                color: GOLD,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <KeyIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: INK,
                }}
              >
                Password
              </Typography>
              {user && (
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 12,
                    color: 'rgba(15, 10, 7, 0.55)',
                  }}
                >
                  Signed in as <strong>{user.email}</strong>
                </Typography>
              )}
            </Box>
          </Box>

          <form onSubmit={handleSubmit} noValidate>
            <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2.25, md: 2.5 } }}>
              <Stack spacing={2.25}>
                {error && <Alert severity="error">{error}</Alert>}

                <FormField label="Current password">
                  <TextField
                    type="password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    autoComplete="current-password"
                    autoFocus
                  />
                </FormField>

                <FormField label="New password" helper="At least 8 characters.">
                  <TextField
                    type="password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                  />
                </FormField>

                <FormField label="Confirm new password">
                  <TextField
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                    error={mismatch}
                    helperText={mismatch ? 'Passwords do not match.' : ' '}
                  />
                </FormField>
              </Stack>
            </Box>

            {/* Footer with actions */}
            <Box
              sx={{
                px: { xs: 2.5, md: 3 },
                py: 1.75,
                borderTop: '1px solid rgba(15, 10, 7, 0.08)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                onClick={() => navigate('/profile')}
                disabled={submitting}
                size="small"
                sx={{ fontFamily: FONT_INTER, fontSize: 12.5, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!valid || submitting}
                variant="contained"
                size="small"
                sx={{
                  background: valid
                    ? `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`
                    : undefined,
                  color: valid ? INK : undefined,
                  fontFamily: FONT_INTER,
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  px: 2,
                  boxShadow: 'none',
                  '&:hover': valid
                    ? {
                        background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                        boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
                      }
                    : undefined,
                }}
              >
                {submitting ? 'Updating…' : 'Update password'}
              </Button>
            </Box>
          </form>
        </Box>
      </Container>
    </SiteLayout>
  );
}

// ---------------------------------------------------------------------
// Field group (label above + helper below)
// ---------------------------------------------------------------------

function FormField({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12,
          fontWeight: 600,
          color: 'rgba(15, 10, 7, 0.75)',
          letterSpacing: '0.02em',
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      {children}
      {helper && (
        <Typography
          sx={{
            mt: 0.5,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.5)',
            lineHeight: 1.5,
          }}
        >
          {helper}
        </Typography>
      )}
    </Box>
  );
}
